import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function DonatePage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("bank");
  const [selectedAmount, setSelectedAmount] = useState<number | null>(150);
  const [customAmount, setCustomAmount] = useState("");

  const donationTiers = [
    { amount: 25, label: t("donatePage.tier.friend.label"), impact: t("donatePage.tier.friend.impact") },
    { amount: 50, label: t("donatePage.tier.supporter.label"), impact: t("donatePage.tier.supporter.impact") },
    { amount: 100, label: t("donatePage.tier.advocate.label"), impact: t("donatePage.tier.advocate.impact") },
    { amount: 150, label: t("donatePage.tier.champion.label"), impact: t("donatePage.tier.champion.impact") },
    { amount: 250, label: t("donatePage.tier.benefactor.label"), impact: t("donatePage.tier.benefactor.impact") },
    { amount: 500, label: t("donatePage.tier.patron.label"), impact: t("donatePage.tier.patron.impact") },
  ];

  const howToDonate = [
    {
      id: "bank",
      label: t("donatePage.bank.title"),
      icon: "ri-bank-line",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">{t("donatePage.bank.desc")}</p>
          <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm space-y-2">
            <div><span className="text-gray-500">{t("donatePage.bank.bank")}:</span> <strong>{t("donatePage.bank.bankName")}</strong></div>
            <div><span className="text-gray-500">{t("donatePage.bank.accountName")}:</span> <strong>{t("donatePage.bank.name")}</strong></div>
            <div><span className="text-gray-500">{t("donatePage.bank.accountNum")}:</span> <strong>123-456-789</strong></div>
            <div><span className="text-gray-500">{t("donatePage.bank.transit")}:</span> <strong>00123</strong></div>
            <div><span className="text-gray-500">{t("donatePage.bank.institution")}:</span> <strong>003</strong></div>
          </div>
          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-800">
            <strong>{t("donatePage.bank.warning")}</strong>
          </div>
        </div>
      ),
    },
    {
      id: "cheque",
      label: t("donatePage.cheque.title"),
      icon: "ri-file-text-line",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">{t("donatePage.cheque.desc")}</p>
          <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm space-y-1">
            <div><strong>{t("donatePage.bank.name")}</strong></div>
            <div>123 Rideau Street, Suite 400</div>
            <div>Ottawa, Ontario K1N 5Y1</div>
            <div>Canada</div>
          </div>
          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-800">
            {t("donatePage.cheque.warning")}
          </div>
        </div>
      ),
    },
    {
      id: "inperson",
      label: t("donatePage.inperson.title"),
      icon: "ri-map-pin-line",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">{t("donatePage.inperson.desc")}</p>
          <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
            <div className="flex items-start gap-2">
              <i className="ri-map-pin-line text-[#3B9EF5] mt-0.5"></i>
              <span>123 Rideau Street, Suite 400, Ottawa, ON K1N 5Y1</span>
            </div>
            <div className="flex items-start gap-2">
              <i className="ri-time-line text-[#3B9EF5] mt-0.5"></i>
              <span>{t("contactPage.office.hoursValue")}</span>
            </div>
            <div className="flex items-start gap-2">
              <i className="ri-phone-line text-[#3B9EF5] mt-0.5"></i>
              <span>+1 (613) 555-0192</span>
            </div>
          </div>
          <p className="text-gray-500 text-xs">{t("donatePage.inperson.note")}</p>
        </div>
      ),
    },
  ];

  return (
    <main className="pt-24">
      {/* Header */}
      <section className="py-16 bg-[#1B2838]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#3B9EF5] mb-3 block">{t("donatePage.tag")}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("donatePage.title")}
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            {t("donatePage.subtitle")}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: Impact */}
            <div>
              <h2 className="text-2xl font-bold text-[#1B2838] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t("donatePage.yourImpact")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {donationTiers.map((tier) => (
                  <button
                    key={tier.amount}
                    onClick={() => { setSelectedAmount(tier.amount); setCustomAmount(""); }}
                    className={`text-left p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      selectedAmount === tier.amount
                        ? "border-[#3B9EF5] bg-[#3B9EF5]/10"
                        : "border-gray-200 bg-white hover:border-[#3B9EF5]/40"
                    }`}
                  >
                    <div className={`text-2xl font-bold mb-1 ${selectedAmount === tier.amount ? "text-[#1B2838]" : "text-[#1B2838]"}`}>
                      ${tier.amount}
                    </div>
                    <div className="text-[#3B9EF5] text-xs font-semibold mb-2">{tier.label}</div>
                    <p className="text-gray-500 text-xs leading-relaxed">{tier.impact}</p>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-5">
                <label className="block text-sm font-semibold text-[#1B2838] mb-3">{t("donatePage.custom.label")}</label>
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <span className="px-4 py-3 bg-gray-50 text-gray-500 font-bold border-r border-gray-200">$</span>
                    <input
                      type="number"
                      min="1"
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                      placeholder={t("donatePage.custom.placeholder")}
                      className="flex-1 px-4 py-3 text-sm outline-none"
                    />
                  </div>
                  <Link
                    to="/contact"
                    className="px-5 py-3 bg-[#3B9EF5] text-[#1B2838] rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-[#2A8DE0] transition-colors"
                  >
                    {t("donatePage.custom.contact")}
                  </Link>
                </div>
                <p className="text-gray-400 text-xs mt-2">{t("donatePage.custom.note")}</p>
              </div>
            </div>

            {/* Right: How to Donate */}
            <div>
              <h2 className="text-2xl font-bold text-[#1B2838] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t("donatePage.howToDonate")}
              </h2>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-gray-100">
                  {howToDonate.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium cursor-pointer transition-all ${
                        activeTab === tab.id
                          ? "text-[#3B9EF5] border-b-2 border-[#3B9EF5]"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <i className={`${tab.icon} text-base`}></i>
                      <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                  ))}
                </div>
                <div className="p-6">
                  {howToDonate.find((tab) => tab.id === activeTab)?.content}
                </div>
              </div>

              {/* Tax Info */}
              <div className="mt-5 bg-[#1B2838] rounded-xl p-5 text-white">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#3B9EF5]/20 text-[#3B9EF5] flex-shrink-0">
                    <i className="ri-file-text-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-sm mb-1">{t("donatePage.tax.title")}</h4>
                    <p className="text-gray-400 text-xs leading-relaxed">
                      {t("donatePage.tax.desc")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-shield-check-line", label: t("donatePage.trust.charity") },
                  { icon: "ri-eye-line", label: t("donatePage.trust.transparent") },
                  { icon: "ri-lock-line", label: t("donatePage.trust.secure") },
                  { icon: "ri-heart-line", label: t("donatePage.trust.programs") },
                ].map((b) => (
                  <div key={b.label} className="flex items-center gap-2 bg-white rounded-lg p-3 border border-gray-100">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#3B9EF5]/10 text-[#3B9EF5]">
                      <i className={`${b.icon} text-sm`}></i>
                    </div>
                    <span className="text-gray-600 text-xs font-medium">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl font-bold text-[#1B2838] mb-3">{t("donatePage.bottom.title")}</h2>
          <p className="text-gray-500 text-sm mb-6">{t("donatePage.bottom.subtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-7 py-3 bg-[#1B2838] text-white rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-[#2a3a4a] transition-colors">
              {t("donatePage.bottom.contact")}
            </Link>
            <a href="mailto:donate@caf-canada.org" className="px-7 py-3 border border-gray-200 text-gray-600 rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer hover:border-[#3B9EF5] hover:text-[#3B9EF5] transition-all">
              donate@caf-canada.org
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}